import { attachmentDisplayName } from "./attachmentDisplayName.ts";
import {
  categoryConnectActivityBodyFragment,
  resolveCategoryConnectKind,
} from "./categoryConnectHub.ts";
import { materialsPedidoDisplayLabel } from "./materialsPedidoDisplayLabel.ts";
import type { ApiNotification } from "./notificationEntityShape.ts";
import type { RegistryEntry } from "./notificationRegistry.ts";
import type {
  HubDetailLine,
  HubDetailVariant,
} from "./notificationTypes.ts";

function str(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") {
    const t = v.trim();
    return t.length ? t : undefined;
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return undefined;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

function messageLikelyMentionsActor(
  message: string,
  actor: string,
  username: string | undefined
): boolean {
  const low = message.toLowerCase();
  const tokens = actor
    .trim()
    .split(/\s+/)
    .filter((w) => w.length >= 3);
  if (tokens.some((t) => low.includes(t.toLowerCase()))) return true;
  if (username) {
    const u = username.trim().toLowerCase();
    if (u.length >= 3 && low.includes(u)) return true;
  }
  return false;
}

function attachActorToSubjectlessActionSentences(
  actor: string,
  message: string
): string {
  const normalized = message.trim();
  if (!normalized) return normalized;
  const pieces = normalized.split(/\.\s+/).filter((p) => p.length > 0);
  const fixed = pieces.map((segment) => {
    const s = segment.trim();
    if (/^Publicó\b/i.test(s)) return s.replace(/^Publicó/i, `${actor} publicó`);
    if (/^Cargó\b/i.test(s)) return s.replace(/^Cargó/i, `${actor} cargó`);
    if (/^Tiene\b/i.test(s)) return s.replace(/^Tiene\b/i, `${actor} tiene`);
    if (/^Se sumó\b/i.test(s)) return s.replace(/^Se sumó/i, `${actor} se sumó`);
    return s;
  });
  let joined = fixed.join(". ");
  if (!/[.!?]$/.test(joined)) joined += ".";
  return joined;
}

function extractCategoryLabelsFromAffairOrMessage(
  affair: string | undefined,
  message: string | undefined
): string | undefined {
  if (affair) {
    const m = affair.match(/\(([^)]+)\)/);
    const inner = m?.[1]?.trim();
    if (inner) return inner;
  }
  if (message) {
    const m = message.match(/relacionada con:\s*([^.]+?)(?:\.|$)/i);
    if (m?.[1]?.trim()) return m[1].trim();
  }
  return undefined;
}

function stripCategoryConnectBoilerplate(message: string): string {
  return message
    .replace(/^Hay actividad nueva en Edify relacionada con:\s*[^.]+\.\s*/i, "")
    .replace(/^Hay actividad nueva[^\n.]+\.\s*/i, "")
    .trim();
}

function buildCoherentCategoryConnectNarrative(
  actor: string,
  affair: string | undefined,
  message: string | undefined,
  ccKind: ReturnType<typeof resolveCategoryConnectKind>,
  senderUsername: string | undefined
): string {
  const cats = extractCategoryLabelsFromAffairOrMessage(affair, message);
  const intro = cats
    ? `Te avisamos porque en tu perfil seguís rubros como ${cats}.`
    : `Te avisamos según los intereses que configuraste en Edify.`;

  if (actor === "Alguien") {
    return `${intro} Hay actividad nueva que puede ser relevante para vos.`;
  }

  if (ccKind) {
    const action = `${actor} ${categoryConnectActivityBodyFragment(ccKind)}`.trim();
    return `${intro} ${action}`.replace(/\s+/g, " ").trim();
  }

  if (message?.trim()) {
    const raw = message.trim();
    if (messageLikelyMentionsActor(raw, actor, senderUsername)) {
      return raw;
    }
    const stripped = stripCategoryConnectBoilerplate(raw);
    const remainder = stripped.length > 0 ? stripped : raw;
    const withActor = attachActorToSubjectlessActionSentences(actor, remainder);
    return `${intro} ${withActor}`.replace(/\s+/g, " ").trim();
  }

  return `${intro} ${actor} tiene actividad nueva en la plataforma.`.replace(
    /\s+/g,
    " "
  ).trim();
}

function downloadableFileHref(
  v: string | number | undefined | null
): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === "number") return null;
  const s = String(v).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/") || s.startsWith("//")) return s;
  if (s.includes("/") || /\.[a-z0-9]{2,5}($|\?|#)/i.test(s)) return s;
  const compact = s.replace(/\s/g, "");
  if (/^[\d.,]+$/.test(compact)) return null;
  return s;
}

function applyTpl(tpl: string, vars: Record<string, string>): string {
  let out = tpl;
  for (const [k, val] of Object.entries(vars)) {
    out = out.split(`{{${k}}}`).join(val);
  }
  return out;
}

function proyectoNombre(n: ApiNotification): string | undefined {
  return (
    str(n.postulacionPermiso?.proyecto?.nombre) ??
    str(n.postulacionPermiso?.proyecto?.name) ??
    str(n.project?.nombre) ??
    str((n.project as { name?: string } | null | undefined)?.name) ??
    str(n.comentario_proyecto?.proyecto?.nombre)
  );
}

function quotationName(n: ApiNotification): string | undefined {
  const top = materialsPedidoDisplayLabel(n.quotation?.name);
  if (top) return top;
  const sqn = materialsPedidoDisplayLabel(n.subQuotationPostulation?.subQuotation?.name);
  if (sqn) return sqn;
  const sqdRaw = str(n.subQuotationPostulation?.subQuotation?.description);
  if (sqdRaw && !/^null$/i.test(sqdRaw)) {
    return materialsPedidoDisplayLabel(sqdRaw);
  }
  return undefined;
}

function jobRequestTitle(n: ApiNotification): string | undefined {
  return str(n.jobRequest?.title) ?? str(n.jobPostulation?.jobRequest?.title);
}

type PostSummary = NonNullable<ApiNotification["post"]>;

function postNestedFromComment(
  c: ApiNotification["comment_post"]
): PostSummary | undefined {
  const pid = c?.post_id;
  if (pid && typeof pid === "object") return pid as PostSummary;
  return undefined;
}

function effectivePostSummary(n: ApiNotification): PostSummary | undefined {
  return n.post ?? postNestedFromComment(n.comment_post);
}

function resolveNotificationPostId(
  n: ApiNotification
): string | number | undefined {
  if (n.post?.id !== undefined && n.post?.id !== null) return n.post.id;
  const pid = n.comment_post?.post_id;
  if (pid === undefined || pid === null) return undefined;
  if (typeof pid === "object") {
    const id = (pid as PostSummary).id;
    if (id !== undefined && id !== null) return id;
    return undefined;
  }
  return pid as number | string;
}

function postDisplay(n: ApiNotification): string | undefined {
  const eff = effectivePostSummary(n);
  const head = str(eff?.titulo) ?? str(eff?.title);
  if (head) return head;
  const ex = str(eff?.content);
  return ex ? truncate(ex, 88) : undefined;
}

function commentSnippet(n: ApiNotification): string | undefined {
  const c = n.comment_post;
  const t = str(c?.contenido) ?? str(c?.content);
  return t ? truncate(t, 140) : undefined;
}

function filterDetailsAgainstBody(
  body: string,
  details: HubDetailLine[]
): HubDetailLine[] {
  const bl = body.toLowerCase();
  const out: HubDetailLine[] = [];
  for (const d of details) {
    const v = str(d.text);
    if (!v) continue;
    if (out.some((x) => x.text === v)) continue;
    const dl = v.toLowerCase();
    if (bl.includes(dl)) continue;
    out.push(d);
    if (out.length >= 6) break;
  }
  return out;
}

export type HubPresentation = {
  title: string;
  body: string;
  details: HubDetailLine[];
  actorAvatar: string | null;
  downloadUrl: string | null;
  downloadLabel: string | null;
};

export function buildHubPresentation(
  n: ApiNotification,
  type: string,
  entry: RegistryEntry,
  actorLabel: string
): HubPresentation {
  const actor = actorLabel || "Alguien";
  const avatar = str(n.userSender?.image_profile) ?? null;
  const affair = str(n.affair);
  const message = str(n.message);
  const genericVars = {
    actor,
    affair: affair ?? "",
    message: message ?? "",
  };

  let title = affair ?? applyTpl(entry.titleTemplate, genericVars);
  let body = message ?? applyTpl(entry.bodyTemplate, genericVars);
  const details: HubDetailLine[] = [];
  const detailAsTextLine =
    /^(Carta de presentación:|Comentario:|Zona de entrega:|Respuesta vinculada)/i;

  const push = (line: string | undefined, variant?: HubDetailVariant) => {
    const v = str(line);
    if (!v) return;
    if (details.some((d) => d.text === v)) return;
    const resolved: HubDetailVariant =
      variant ??
      (v.length > 92 || detailAsTextLine.test(v) ? "text" : "chip");
    details.push({ text: v, variant: resolved });
  };

  const pn = proyectoNombre(n);
  const qn = quotationName(n);
  const subDesc = str(n.subProyectoPresupuesto?.descripcion);
  const skillName = str(n.skill?.name);
  const jobTitle = jobRequestTitle(n);
  const fileName = str(n.subQuotationPostulation?.file);
  const obraNombre = str(n.project?.nombre);
  const pq = n.portfolio_question;
  const pqText = str(pq?.question) ?? str(pq?.text);
  const postTitulo = postDisplay(n);
  const postId = resolveNotificationPostId(n);
  const comSnippet = commentSnippet(n);

  switch (type) {
    case "COMMENT_SENT":
      if (!message && pn) body = `${actor} hizo una consulta sobre «${pn}».`;
      if (pn) push(`Obra: ${pn}`);
      break;
    case "COMMENT_REPLIED":
      if (!message && pn) body = `${actor} respondió en «${pn}».`;
      if (pn) push(`Obra: ${pn}`);
      break;
    case "APPLICATION_SENT": {
      const permSt = (n.postulacionPermiso?.status ?? "")
        .toString()
        .trim()
        .toUpperCase();
      const negated = permSt === "NEGATED" || permSt === "DENIED";
      const allowed = permSt === "ALLOWED";
      if (negated) {
        if (!affair) {
          title = pn ? truncate(`Postulación denegada · ${pn}`, 52) : "Postulación denegada";
        }
        if (!message) {
          body = pn
            ? `Denegaste la postulación de ${actor} a la obra «${pn}».`
            : `Denegaste la postulación de ${actor}.`;
        }
      } else if (allowed) {
        if (!affair) {
          title = pn ? truncate(`Postulación aceptada · ${pn}`, 52) : "Postulación aceptada";
        }
        if (!message) {
          body = pn
            ? `Aceptaste la postulación de ${actor} a la obra «${pn}».`
            : `Aceptaste la postulación de ${actor}.`;
        }
      } else if (!message && pn) {
        body = `${actor} quiere sumarse a «${pn}».`;
      }
      if (pn) push(`Obra: ${pn}`);
      break;
    }
    case "APPLICATION_ACCEPTED":
      if (!message && pn) body = `Te aceptaron en «${pn}».`;
      if (pn) push(`Obra: ${pn}`);
      break;
    case "APPLICATION_REJECTED":
      if (!message && pn) body = `La postulación a «${pn}» no avanzó.`;
      if (pn) push(`Obra: ${pn}`);
      break;
    case "BUDGET_SENT": {
      const href = downloadableFileHref(n.subProyectoPresupuesto?.budget);
      if (!message) {
        if (subDesc) body = `${actor} envió un presupuesto para el rubro «${subDesc}».`;
        else body = `${actor} envió un presupuesto para tu obra.`;
      }
      if (subDesc) push(`Rubro: ${subDesc}`);
      if (href) push(`Archivo: ${attachmentDisplayName(href)}`);
      break;
    }
    case "BUDGET_ACCEPTED":
      if (!message) {
        if (subDesc) body = `Aceptaron tu presupuesto de «${subDesc}».`;
        else body = `Aceptaron tu presupuesto para un rubro de la obra.`;
      }
      if (subDesc) push(`Rubro: ${subDesc}`);
      break;
    case "BUDGET_REJECTED":
      if (!message) {
        if (subDesc) body = `No se avanzó con el presupuesto de «${subDesc}».`;
        else body = `No se avanzó con un presupuesto que habías enviado.`;
      }
      if (subDesc) push(`Rubro: ${subDesc}`);
      break;
    case "QUOTATION_SENT":
    case "QUOTATION_MATERIALS_UPDATED":
      if (qn) {
        if (!affair) title = truncate(qn, 52);
        if (!message) {
          body =
            type === "QUOTATION_MATERIALS_UPDATED"
              ? `${actor} actualizó la oferta de materiales del pedido «${qn}». Revisá el presupuesto y el detalle.`
              : `${actor} te envió un presupuesto de materiales para el pedido «${qn}».`;
        }
        if (!body.toLowerCase().includes(qn.toLowerCase())) {
          push(`Pedido: ${qn}`);
        }
      } else if (!message) {
        body =
          type === "QUOTATION_MATERIALS_UPDATED"
            ? `${actor} actualizó la oferta de materiales de un pedido en el que participás. Revisá el presupuesto.`
            : `${actor} te envió un presupuesto de materiales para tu pedido de cotización.`;
      }
      break;
    case "USER_APPLICATION_SENT":
      if (str(n.userSender?.username)) push(`Usuario: ${str(n.userSender?.username)}`);
      break;
    case "USER_FEATURED_APPLICATION_SENT":
      if (!message) {
        body = `${actor} envió una solicitud para aparecer destacado.`;
      }
      if (str(n.userSender?.username)) push(`Usuario: ${str(n.userSender?.username)}`);
      break;
    case "USER_APPLICATION_ACCEPTED":
    case "USER_APPLICATION_REJECTED":
      break;
    case "PROFILE_VIEW_RECEIVED":
      if (!message) body = `${actor} visitó tu perfil.`;
      break;
    case "POST_LIKED":
      if (!message) {
        if (postTitulo) body = `${actor} le dio me gusta a «${truncate(postTitulo, 70)}».`;
        else body = `${actor} le dio me gusta a una de tus publicaciones.`;
      } else if (!affair && postTitulo) {
        title = truncate(postTitulo, 52);
      }
      if (!message) {
        if (postTitulo) push(`Publicación: ${truncate(postTitulo, 80)}`);
        else if (postId) push(`Publicación n.º ${postId}`);
      }
      break;
    case "POST_COMMENTED":
      if (!message) {
        if (postTitulo) body = `${actor} comentó «${truncate(postTitulo, 60)}».`;
        else body = `${actor} comentó una de tus publicaciones.`;
      } else if (!affair && postTitulo) {
        title = truncate(postTitulo, 52);
      }
      if (!message) {
        if (postTitulo) push(`Publicación: ${truncate(postTitulo, 80)}`);
        if (comSnippet) push(`Comentario: ${comSnippet}`);
      }
      break;
    case "POST_COMMENT_LIKE":
      if (!message) {
        if (postTitulo) body = `${actor} destacó un comentario en «${truncate(postTitulo, 50)}».`;
        else body = `${actor} le dio me gusta a un comentario en tu publicación.`;
      } else if (!affair && postTitulo) {
        title = truncate(postTitulo, 52);
      }
      if (!message) {
        if (comSnippet) push(`Comentario: ${comSnippet}`);
        else if (postId) push(`Publicación n.º ${postId}`);
      }
      break;
    case "POST_COMMENT_REPLIED":
      if (!message) {
        if (postTitulo) body = `${actor} respondió en «${truncate(postTitulo, 50)}».`;
        else body = `${actor} respondió un comentario en tu publicación.`;
      } else if (!affair && postTitulo) {
        title = truncate(postTitulo, 52);
      }
      if (!message && comSnippet) {
        push(`Respuesta vinculada al comentario: ${comSnippet}`);
      }
      break;
    case "NEW_CONTACT_REQUEST":
      if (message) {
        if (!affair) title = `Mensaje de ${actor}`;
        body = message;
        push(`Te escribió: ${actor}`);
      }
      break;
    case "NEW_MATCHES":
      if (!message) {
        if (skillName && obraNombre) {
          body = `${actor} publicó una obra que coincide con tu rubro «${skillName}»: «${obraNombre}». Revisá los detalles y postuláte.`;
        } else if (skillName) {
          body = `Hay una obra nueva que coincide con ${skillName}.`;
        } else if (obraNombre) {
          body = `Hay actividad nueva en «${obraNombre}».`;
        }
      }
      if (skillName && (!message || !body.toLowerCase().includes(skillName.toLowerCase()))) {
        push(`Especialidad: ${skillName}`);
      }
      if (obraNombre && (!message || !body.toLowerCase().includes(obraNombre.toLowerCase()))) {
        push(`Obra: ${obraNombre}`);
      }
      break;
    case "NEW_JOB_APPLICATION":
      if (!affair && jobTitle) {
        title = truncate(`Nueva postulación · ${jobTitle}`, 52);
      } else if (!affair) {
        title = "Nueva postulación laboral";
      }
      if (!message) {
        body = `${actor} se postuló a tu aviso laboral`;
      }
      break;
    case "QUESTION_RECEIVED":
      if (pqText) {
        if (!message) body = `Te dejaron esta consulta: «${truncate(pqText, 160)}»`;
      } else if (!message) {
        body = `Recibiste una nueva consulta sobre tu portfolio.`;
      }
      break;
    case "QUESTION_REPLIED":
      if (!message) {
        if (pqText) body = `${actor} respondió: «${truncate(pqText, 140)}»`;
        else body = `${actor} respondió una consulta de tu portfolio.`;
      }
      break;
    case "PROFILE_REVIEW_RECEIVED": {
      const r = n.review;
      const stars =
        r &&
        (typeof r.stars === "number"
          ? r.stars
          : typeof r.rating === "number"
            ? r.rating
            : typeof r.score === "number"
              ? r.score
              : undefined);
      const reviewText = r ? str(r.comment) ?? str(r.text) : undefined;
      if (typeof stars === "number" && Number.isFinite(stars)) {
        push(`Valoración: ${stars} de 5`, "chip");
      }
      if (reviewText) {
        const excerpt = truncate(reviewText, 255);
        const hint = excerpt.slice(0, Math.min(48, excerpt.length)).toLowerCase();
        if (!hint || !body.toLowerCase().includes(hint)) {
          push(`Comentario: «${excerpt}»`, "text");
        }
      }
      break;
    }
    case "CATEGORY_CONNECT_ACTIVITY": {
      const ccKind = resolveCategoryConnectKind(n);
      const senderUsername = str(n.userSender?.username);
      title = affair
        ? truncate(affair, 96)
        : truncate(applyTpl(entry.titleTemplate, genericVars), 96);
      body = buildCoherentCategoryConnectNarrative(
        actor,
        affair,
        message,
        ccKind,
        senderUsername
      );
      break;
    }
    case "MATERIAL_DEMAND_ALERT":
    case "GENERIC_MATERIAL_DEMAND_ALERT": {
      const zone = str(n.quotation?.zone);
      const qidRaw = n.quotation?.id;
      const qidStr =
        qidRaw !== undefined && qidRaw !== null ? String(qidRaw) : undefined;
      if (qn) {
        if (!affair) title = truncate(`Demanda: ${qn}`, 52);
        if (!message) body = `Hay actividad en un pedido que podría interesarte: «${qn}».`;
        if (!message || !body.toLowerCase().includes(qn.toLowerCase())) {
          push(`Pedido: ${qn}`);
        }
      }
      if (qidStr && (!message || !body.includes(qidStr))) {
        push(`Cotización n.º ${qidStr}`);
      }
      if (
        zone &&
        (!message || !body.toLowerCase().includes(zone.slice(0, 24).toLowerCase()))
      ) {
        push(`Zona de entrega: ${truncate(zone, 140)}`);
      }
      break;
    }
    case "COMMENT_RECEIVED":
    case "QUOTATION_ACCEPTED":
    case "QUOTATION_UPDATED":
      if (qn) {
        title = truncate(qn, 52);
        if (!message) body = `Actualización vinculada a «${qn}».`;
        push(`Cotización: ${qn}`);
      }
      if (fileName) push(`Adjunto: ${attachmentDisplayName(fileName)}`);
      break;
    default:
      break;
  }

  const detailsOut = filterDetailsAgainstBody(body, details);

  let downloadUrl: string | null = null;
  let downloadLabel: string | null = null;
  if (
    type === "BUDGET_SENT" ||
    type === "BUDGET_ACCEPTED" ||
    type === "BUDGET_REJECTED"
  ) {
    downloadUrl = downloadableFileHref(n.subProyectoPresupuesto?.budget);
    if (downloadUrl) downloadLabel = "Descargar presupuesto";
  }
  if (type === "QUOTATION_SENT" || type === "QUOTATION_MATERIALS_UPDATED") {
    const qFile = downloadableFileHref(n.subQuotationPostulation?.file);
    if (qFile) {
      downloadUrl = qFile;
      downloadLabel = "Descargar presupuesto";
    }
  }

  const titleMax = type === "CATEGORY_CONNECT_ACTIVITY" ? 96 : 72;

  return {
    title: truncate(title, titleMax),
    body,
    details: detailsOut,
    actorAvatar: avatar,
    downloadUrl,
    downloadLabel,
  };
}
